import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import {
  Dictionary,
  isArray,
  isEmpty,
  isEqual,
  isNumber,
  isObject,
  sortBy,
  uniqBy,
} from 'lodash';
import { UseFormReset } from 'react-hook-form';
import { FileUploadStatus } from 'api/attachments';
import {
  FormAnswerMetaJson,
  FormAnswerTypes,
  FormQuestion,
  FormQuestionType,
  UploadFormAnswerImage,
} from 'api/form';
import { convertFileAttachmentToUploadFileWithAttachment } from 'components/molecules/forms/form-fields/attachment-field';
import {
  addUpdateFormResponseDraftIndexedDb,
  deleteFormResponseFromIndexedDb,
  getFormResponseDraftFromIndexedDb,
} from 'hooks/use-idb';

export const computePhoneNumbersList = (): {
  label: string;
  value: string;
}[] => {
  const countries = getCountries();

  // Typescript doesn't have types for DisplayNames yet and that's why the type assertion "any" is needed below.
  // This will be fixed in typescript update https://github.com/microsoft/TypeScript/pull/44022
  const regionNames = new (Intl as any).DisplayNames(['en'], {
    type: 'region',
  });

  return sortBy(
    uniqBy(
      countries.map((country) => {
        const countryCode = `+${getCountryCallingCode(country)}`;

        return {
          label:
            countryCode === '+1' ? 'United States' : regionNames.of(country),
          value: countryCode,
        };
      }),
      'value',
    ),
    'value',
  );
};

export const computeFormDefaultValues = (
  formAnswerData: FormAnswerMetaJson,
): { [key: string]: string | number } => {
  const updatedData = {};

  Object.entries(formAnswerData).forEach(([key, value]) => {
    updatedData[key] =
      value.type === FormAnswerTypes.Attachment
        ? convertFileAttachmentToUploadFileWithAttachment(
            value.answers as AttachmentFieldUploadData[],
          )
        : value.answers;
  });

  return updatedData;
};

export interface AttachmentFieldUploadData extends UploadFormAnswerImage {
  attachmentId: number;
  location: string;
  name: string;
  publicVisibility: boolean;
  relativePath: string;
  size: number;
  sizeInBytes: number;
  status: FileUploadStatus;
  thumbLocation: string;
  thumbUrl: string;
  type: 'TYPE';
  createdBy: number;
  createdAt: number;
}

export type FormResponse = {
  [key: number]:
    | number
    | string
    | number[]
    | AttachmentFieldUploadData[]
    | undefined;
};

export const validateAttachmentUploading = (data: FormResponse) => {
  return Object.values(data).some((answerList) => {
    if (isArray(answerList) && answerList.length) {
      let attachmentUploading = false;

      (answerList as AttachmentFieldUploadData[]).forEach((answer) => {
        if (isObject(answer) && answer.status === FileUploadStatus.Uploading) {
          attachmentUploading = true;
        }
      });

      return attachmentUploading;
    }

    return false;
  });
};

const computeQuestionType = (formQuestion: FormQuestion) => {
  if (formQuestion?.formQuestionType === FormQuestionType.StarRating) {
    if (formQuestion?.projectFieldId) {
      return FormAnswerTypes.Decimal;
    }

    return FormAnswerTypes.Number;
  }
  switch (formQuestion?.formQuestionType) {
    case FormQuestionType.TextShort:
    case FormQuestionType.TextLong:
    case FormQuestionType.Date:
    case FormQuestionType.Email:
    case FormQuestionType.Phone:
      return FormAnswerTypes.Text;
    case FormQuestionType.Number:
    case FormQuestionType.Currency:
      return FormAnswerTypes.Decimal;
    case FormQuestionType.ChoiceDropdown:
    case FormQuestionType.ChoiceRadio:
      return FormAnswerTypes.Number;
    case FormQuestionType.ChoiceDropdownMultiselect:
    case FormQuestionType.ChoiceCheckbox:
      return FormAnswerTypes.Choices;
    case FormQuestionType.CheckBoxGrid:
      return FormAnswerTypes.MatrixRadio;
    case FormQuestionType.MultipleChoiceGrid:
      return FormAnswerTypes.MatrixCheckbox;
    case FormQuestionType.Attachment:
      return FormAnswerTypes.Attachment;
    default:
      return null;
  }
};

export const computeFormAnswerPayload = (
  data: FormResponse,
  flattenedQuestionsMap: Dictionary<FormQuestion>,
): FormAnswerMetaJson => {
  const formAnswerPayload = {} as FormAnswerMetaJson;

  Object.entries(data).forEach(([key, result]) => {
    const questionId = parseInt(key, 10);

    const isValuePresent = isNumber(result) ? result : !isEmpty(result);
    const questionType = computeQuestionType(flattenedQuestionsMap[questionId]);

    if (isValuePresent && questionType) {
      if (
        isObject(result) &&
        flattenedQuestionsMap[questionId].formQuestionType ===
          FormQuestionType.Attachment
      ) {
        // For attachment fields, return only the attachmentId
        if ((result as UploadFormAnswerImage[]).length > 0) {
          formAnswerPayload[key] = {
            questionId,
            type: questionType,
            answers: (result as UploadFormAnswerImage[]).reduce(
              (acc, currentAttachment) => {
                if (currentAttachment?.attachment?.attachmentId) {
                  acc.push({
                    attachmentId: currentAttachment.attachment.attachmentId,
                  });
                }

                return acc;
              },
              [] as { attachmentId: number }[],
            ),
          };
        }
      } else {
        formAnswerPayload[key] = {
          questionId,
          type: questionType,
          answers:
            questionType === FormAnswerTypes.Decimal
              ? String(result)
              : (result as string | number),
        };
      }
    }
  });

  return formAnswerPayload;
};

export const setValuesInForm = async (
  formInstanceId: number | undefined,
  updatedAt: number | undefined,
  formDefaultValues: { [key: string]: string | number } | undefined,
  reset: UseFormReset<Record<string, string | number | number[]>>,
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>,
  setShowBanner: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  if (formInstanceId != null) {
    const res = await getFormResponseDraftFromIndexedDb(formInstanceId);

    if (res != null) {
      if (res?.timestamp > (updatedAt ?? 0)) {
        let response = {};

        Object.keys(res.response).forEach((x) => {
          if (res.response[x] != null) {
            response = { ...response, [x]: res.response[x] };
          }
        });
        reset(response);
        if (!(isEmpty(response) && isEmpty(formDefaultValues))) {
          if (!isEqual(response, formDefaultValues)) {
            setIsEditing(true);
            setShowBanner(true);
          }
        }
      } else {
        await deleteFormResponseFromIndexedDb(formInstanceId);
      }
    }
  }
};

export const resetQuestionsToEmptyValuesUtil = (
  flattenedQuestionsMap: Dictionary<FormQuestion> | undefined,
  reset: UseFormReset<Record<string, string | number | number[]>>,
) => {
  const values = Object.keys(flattenedQuestionsMap || []).reduce(
    (acc, questionId) => {
      const { formQuestionType } = flattenedQuestionsMap
        ? flattenedQuestionsMap[questionId]
        : { formQuestionType: '' };

      if (
        formQuestionType === 'MATRIX_ROW' ||
        formQuestionType === 'MATRIX_CHECKBOX' ||
        formQuestionType === 'MATRIX_RADIO'
      ) {
        acc[questionId] = {};
      } else {
        acc[questionId] = '';
      }

      return acc;
    },
    {},
  );

  reset(values);
};

export const saveFormRespToIndexedDb = async (
  data: FormResponse,
  isMultiResponseMode: boolean,
  instanceId: number,
  formResponseId?: number,
) => {
  if (isMultiResponseMode) {
    if (formResponseId) {
      // if a multi response form is already submitted, we shouldn't display the edited values in Idb as that might conflict new values
      return;
    }
    if (Object.values(data).every((x) => x === '' || x == null)) {
      return;
    }
  }
  let response = {};

  Object.keys(data).forEach((x) => {
    if (data[x] != null) {
      response = { ...response, [x]: data[x] };
    }
  });
  if (!isEmpty(response)) {
    await addUpdateFormResponseDraftIndexedDb(instanceId, data);
  }
};
